import { Injectable } from '@angular/core';
import { DirectusService } from './directus.service';
import {
  AggregationMode,
  DATE_PRESET_OPTIONS,
  DatePreset,
  DatePresetFilter,
  ReportDefinition,
  ReportFilter,
  ReportMetric,
  ReportMode,
  ReportResult,
  ReportResultRow,
  ReportSource,
  ReportViz,
  ResolvedField,
} from '../models/report.model';

const COLLECTION = 'reports';
const LEGACY_KEY  = 'reports_v1';   // localStorage key from v1 — migrated on first load

@Injectable({ providedIn: 'root' })
export class ReportService {
  private readonly fieldCache = new Map<string, ResolvedField[]>();
  private collectionCache: string[] = [];
  /** IDs that are confirmed to exist in Directus (populated by list()). */
  private serverIds = new Set<string>();

  constructor(private readonly directus: DirectusService) {}

  // ── Schema discovery ────────────────────────────────────────────────────

  async loadCollections(): Promise<string[]> {
    if (this.collectionCache.length) return this.collectionCache;
    try {
      const all = await this.directus.listCollections();
      this.collectionCache = all
        .filter(c =>
          !c.collection.startsWith('directus_') &&
          c.meta?.hidden !== true &&
          c.meta?.singleton !== true
        )
        .map(c => c.collection)
        .sort((a, b) => a.localeCompare(b));
    } catch {
      this.collectionCache = [];
    }
    return this.collectionCache;
  }

  async loadFields(collection: string): Promise<ResolvedField[]> {
    if (this.fieldCache.has(collection)) return this.fieldCache.get(collection)!;

    let fields: ResolvedField[] = [];
    try {
      const raw = await this.directus.listFields(collection);
      const direct: ResolvedField[] = raw
        .filter(f =>
          f.type !== 'alias' &&
          f.meta?.hidden !== true &&
          !f.schema?.is_primary_key
        )
        .map(f => ({
          key: f.field,
          label: this.humanise(f.field),
          type: this.mapType(f.type),
          numeric: this.mapType(f.type) === 'number',
          relatedCollection: f.schema?.foreign_key_table ?? undefined,
        }));

      // One level of M2O dot-notation expansion
      const relatedPromises = direct
        .filter(f => f.relatedCollection)
        .map(async f => {
          try {
            const relRaw = await this.directus.listFields(f.relatedCollection!);
            return relRaw
              .filter(rf =>
                rf.type !== 'alias' &&
                rf.meta?.hidden !== true &&
                !rf.schema?.is_primary_key &&
                !rf.schema?.foreign_key_table
              )
              .map<ResolvedField>(rf => ({
                key: `${f.key}.${rf.field}`,
                label: `${this.humanise(f.key)} → ${this.humanise(rf.field)}`,
                type: this.mapType(rf.type),
                numeric: this.mapType(rf.type) === 'number',
              }));
          } catch { return []; }
        });

      const related = (await Promise.all(relatedPromises)).flat();
      fields = [...direct, ...related];
    } catch {
      fields = [];
    }

    this.fieldCache.set(collection, fields);
    return fields;
  }

  // ── Directus CRUD ───────────────────────────────────────────────────────

  async list(): Promise<ReportDefinition[]> {
    const rows = await this.directus.list(COLLECTION, {
      limit: -1,
      sort: '-date_updated',
      fields: 'id,name,mode,aggregation_mode,sources,group_by,viz,sort_desc,top_n,date_created,date_updated',
    });
    this.serverIds = new Set(rows.map(r => r['id'] as string));

    const reports = rows.map(r => this.fromDirectus(r));

    // One-time migration from localStorage v1
    this.migrateLegacy(reports);

    return reports;
  }

  async save(report: ReportDefinition): Promise<ReportDefinition> {
    const payload = this.toDirectus(report);
    let result: Record<string, unknown>;
    if (this.serverIds.has(report.id)) {
      result = await this.directus.update(COLLECTION, report.id, payload);
    } else {
      result = await this.directus.create(COLLECTION, { ...payload, id: report.id });
      this.serverIds.add(report.id);
    }
    return this.fromDirectus(result);
  }

  async delete(id: string): Promise<void> {
    await this.directus.remove(COLLECTION, id);
    this.serverIds.delete(id);
  }

  // ── Date preset resolution ───────────────────────────────────────────────

  resolvePreset(preset: DatePreset): { gte?: string; lte?: string } {
    const now = new Date();

    const iso = (d: Date) => d.toISOString();
    const startOf = (d: Date) => { d.setHours(0, 0, 0, 0); return d; };
    const endOf   = (d: Date) => { d.setHours(23, 59, 59, 999); return d; };

    switch (preset) {
      case 'today':
        return { gte: iso(startOf(new Date(now))), lte: iso(endOf(new Date(now))) };

      case 'yesterday': {
        const d = new Date(now); d.setDate(d.getDate() - 1);
        return { gte: iso(startOf(new Date(d))), lte: iso(endOf(new Date(d))) };
      }
      case 'this_week': {
        const d = new Date(now); d.setDate(d.getDate() - d.getDay());
        return { gte: iso(startOf(d)) };
      }
      case 'last_week': {
        const end = new Date(now); end.setDate(end.getDate() - end.getDay() - 1);
        const start = new Date(end); start.setDate(start.getDate() - 6);
        return { gte: iso(startOf(start)), lte: iso(endOf(end)) };
      }
      case 'this_month':
        return { gte: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };

      case 'last_month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end   = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return { gte: iso(start), lte: iso(end) };
      }
      case 'last_30_days': {
        const d = new Date(now); d.setDate(d.getDate() - 30);
        return { gte: iso(startOf(d)) };
      }
      case 'last_3_months': {
        const d = new Date(now); d.setMonth(d.getMonth() - 3);
        return { gte: iso(startOf(d)) };
      }
      case 'last_6_months': {
        const d = new Date(now); d.setMonth(d.getMonth() - 6);
        return { gte: iso(startOf(d)) };
      }
      case 'this_year':
        return { gte: iso(new Date(now.getFullYear(), 0, 1)) };

      case 'last_year': {
        const start = new Date(now.getFullYear() - 1, 0, 1);
        const end   = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
        return { gte: iso(start), lte: iso(end) };
      }
    }
  }

  // ── Query building ──────────────────────────────────────────────────────

  buildQuery(
    source: ReportSource,
    groupBy: string | null
  ): Record<string, string | number | boolean | undefined> {
    const q: Record<string, string | number | boolean | undefined> = {};

    // Regular filters
    for (const f of source.filters) {
      if (f.operator === '_null' || f.operator === '_nnull') {
        q[`filter[${f.field}][${f.operator}]`] = true;
      } else {
        q[`filter[${f.field}][${f.operator}]`] = f.value;
      }
    }

    // Date preset filters — resolved to actual ISO strings at run time
    for (const dp of (source.datePresets ?? [])) {
      const { gte, lte } = this.resolvePreset(dp.preset);
      if (gte) q[`filter[${dp.field}][_gte]`] = gte;
      if (lte) q[`filter[${dp.field}][_lte]`] = lte;
    }

    // Metric
    if (source.metric.type === 'count')      q['aggregate[count]'] = '*';
    else if (source.metric.type === 'sum')   q['aggregate[sum]']   = source.metric.field;
    else if (source.metric.type === 'avg')   q['aggregate[avg]']   = source.metric.field;

    // Group-by
    if (groupBy) { q['groupBy[]'] = groupBy; q['limit'] = -1; }
    else           q['limit'] = 1;

    return q;
  }

  // ── Run ─────────────────────────────────────────────────────────────────

  async run(def: ReportDefinition): Promise<ReportResult> {
    const mode = def.aggregationMode ?? 'server';
    if (mode === 'client') {
      return this.runClientAggregation({
        ...def,
        sortDesc: def.sortDesc ?? true,
        topN: def.topN ?? 10,
      });
    }
    return this.runServerAggregation(def);
  }

  // ── Private: client-side aggregation ────────────────────────────────────

  private async runClientAggregation(def: ReportDefinition): Promise<ReportResult> {
    const source = def.sources[0];
    const groupByField = def.groupBy;
    const displayCols = source.displayColumns ?? [];

    const fieldsToFetch: string[] = [];
    if (groupByField) fieldsToFetch.push(groupByField);
    if (source.metric.type !== 'count' && source.metric.field) {
      fieldsToFetch.push(source.metric.field);
    }
    // Include display columns in the fields fetch
    for (const dc of displayCols) {
      if (!fieldsToFetch.includes(dc)) fieldsToFetch.push(dc);
    }

    const q: Record<string, string | number | boolean | undefined> = {};
    for (const f of source.filters) {
      if (f.operator === '_null' || f.operator === '_nnull') {
        q[`filter[${f.field}][${f.operator}]`] = true;
      } else {
        q[`filter[${f.field}][${f.operator}]`] = f.value;
      }
    }
    for (const dp of (source.datePresets ?? [])) {
      const { gte, lte } = this.resolvePreset(dp.preset);
      if (gte) q[`filter[${dp.field}][_gte]`] = gte;
      if (lte) q[`filter[${dp.field}][_lte]`] = lte;
    }
    q['limit'] = -1;
    if (fieldsToFetch.length) q['fields[]'] = fieldsToFetch.join(',');

    const rawRows = await this.directus.list(source.collection, q);

    const grouped = new Map<string, number>();
    // Track the first raw row per label so we can show display column values
    const firstRowByLabel = new Map<string, Record<string, unknown>>();
    for (const row of rawRows) {
      const label = groupByField ? this.resolveNestedValue(row, groupByField) : 'Total';
      if (!firstRowByLabel.has(label)) firstRowByLabel.set(label, row);
      const existing = grouped.get(label) ?? 0;
      if (source.metric.type === 'count') {
        grouped.set(label, existing + 1);
      } else {
        const val = source.metric.field ? this.resolveNestedValue(row, source.metric.field) : '0';
        grouped.set(label, existing + Number(val || 0));
      }
    }

    let entries = [...grouped.entries()];
    entries.sort((a, b) => def.sortDesc ? b[1] - a[1] : a[1] - b[1]);
    if (def.topN > 0) entries = entries.slice(0, def.topN);

    const maxVal = Math.max(...entries.map(([, v]) => v), 1);

    // Resolve display column labels from the field cache
    const cachedFields = this.fieldCache.get(source.collection) ?? [];
    const displayColumnLabels = displayCols.map(dc => {
      const f = cachedFields.find(rf => rf.key === dc);
      return f ? f.label : this.humanise(dc.split('.').pop() ?? dc);
    });

    const rows: ReportResultRow[] = entries.map(([label, value]) => {
      const firstRow = firstRowByLabel.get(label);
      return {
        label,
        values: [value],
        pct: [Math.round((value / maxVal) * 100)],
        displayValues: displayCols.map(dc =>
          firstRow ? this.resolveNestedValue(firstRow, dc) : ''
        ),
      };
    });

    const metricLabel = source.metric.type === 'count'
      ? 'Count'
      : `${source.metric.type === 'sum' ? 'Sum' : 'Avg'} of ${this.humanise(source.metric.field ?? '')}`;

    return {
      rows,
      seriesLabels: [source.label],
      metricLabels: [metricLabel],
      groupByLabel: groupByField ? this.humanise(groupByField.split('.').pop() ?? groupByField) : 'Total',
      rawData: [rawRows],
      displayColumnLabels,
    };
  }

  // ── Private: server-side aggregation ────────────────────────────────────

  private async runServerAggregation(def: ReportDefinition): Promise<ReportResult> {
    const rawResults = await Promise.all(
      def.sources.map(source =>
        this.directus.list(source.collection, this.buildQuery(source, def.groupBy))
      )
    );

    const metricField = (s: ReportSource) => s.metric.field ?? '*';
    const metricLabels = def.sources.map(s =>
      s.metric.type === 'count'
        ? 'Count'
        : `${s.metric.type === 'sum' ? 'Sum' : 'Avg'} of ${this.humanise(metricField(s))}`
    );

    const normalised = rawResults.map((rawRows, si) => {
      const s = def.sources[si];
      const mKey   = s.metric.type;
      const mField = metricField(s);
      return rawRows.map(row => ({
        label: this.extractLabel(row, def.groupBy),
        value: Number((row[mKey] as Record<string, unknown>)?.[mField] ?? 0),
      }));
    });

    const allLabels = [...new Set(normalised.flatMap(r => r.map(x => x.label)))];
    allLabels.sort((a, b) => {
      const na = Number(a), nb = Number(b);
      return (!isNaN(na) && !isNaN(nb)) ? na - nb : a.localeCompare(b);
    });

    const seriesMax = normalised.map(r => Math.max(...r.map(x => x.value), 1));
    const rows: ReportResultRow[] = allLabels.map(label => {
      const values = normalised.map(r => r.find(x => x.label === label)?.value ?? 0);
      return { label, values, pct: values.map((v, i) => Math.round((v / seriesMax[i]) * 100)) };
    });

    return {
      rows,
      seriesLabels: def.sources.map(s => s.label),
      metricLabels,
      groupByLabel: this.deriveGroupByLabel(def.groupBy),
      rawData: rawResults,
      displayColumnLabels: [],
    };
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  freshDraft(): ReportDefinition {
    return {
      id: crypto.randomUUID(),
      name: '',
      mode: 'single',
      aggregationMode: 'server',
      sources: [this.freshSource()],
      groupBy: null,
      sortDesc: true,
      topN: 10,
      viz: 'table',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  freshSource(collection = ''): ReportSource {
    return { collection, label: 'Series', filters: [], datePresets: [], metric: { type: 'count' }, displayColumns: [] };
  }

  freshFilter(): ReportFilter {
    return { field: '', operator: '_eq', value: '' };
  }

  freshDatePreset(field = ''): DatePresetFilter {
    return { field, preset: 'this_month' };
  }

  metricLabel(metric: ReportMetric): string {
    if (metric.type === 'count') return 'Count';
    return `${metric.type === 'sum' ? 'Sum' : 'Avg'} of ${this.humanise(metric.field ?? '')}`;
  }

  summaryLine(def: ReportDefinition): string {
    const collections = [...new Set(def.sources.map(s => s.collection))].join(', ');
    const metrics = def.sources.map(s => this.metricLabel(s.metric)).join(' / ');
    return `${collections} — ${metrics}`;
  }

  presetLabel(preset: DatePreset): string {
    return DATE_PRESET_OPTIONS.find(o => o.value === preset)?.label ?? preset;
  }

  // ── Directus field mappers ───────────────────────────────────────────────

  private toDirectus(def: ReportDefinition): Record<string, unknown> {
    return {
      name:             def.name,
      mode:             def.mode,
      aggregation_mode: def.aggregationMode,
      sources:          def.sources,
      group_by:         def.groupBy,
      viz:              def.viz,
      sort_desc:        def.sortDesc,
      top_n:            def.topN,
    };
  }

  private fromDirectus(row: Record<string, unknown>): ReportDefinition {
    const sources = (row['sources'] as ReportSource[] | null) ?? [];
    // Backfill fields added in later versions
    const normSources = sources.map(s => ({
      ...s,
      datePresets: s.datePresets ?? [],
      displayColumns: s.displayColumns ?? [],
    }));

    return {
      id:              row['id'] as string,
      name:            row['name'] as string ?? '',
      mode:           (row['mode'] as ReportMode)           ?? 'single',
      aggregationMode:(row['aggregation_mode'] as AggregationMode) ?? 'server',
      sources:         normSources,
      groupBy:        (row['group_by'] as string | null)    ?? null,
      viz:            (row['viz'] as ReportViz)              ?? 'table',
      sortDesc:       (row['sort_desc'] as boolean)         ?? true,
      topN:           (row['top_n'] as number)              ?? 10,
      createdAt:      (row['date_created'] as string)       ?? new Date().toISOString(),
      updatedAt:      (row['date_updated'] as string)       ?? new Date().toISOString(),
    };
  }

  // ── One-time localStorage migration ─────────────────────────────────────

  private migrateLegacy(serverReports: ReportDefinition[]): void {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return;
      const legacy = JSON.parse(raw) as ReportDefinition[];
      if (!legacy?.length) { localStorage.removeItem(LEGACY_KEY); return; }

      const serverIdSet = new Set(serverReports.map(r => r.id));
      const toMigrate = legacy.filter(r => !serverIdSet.has(r.id));

      // Fire-and-forget: migrate each legacy report to Directus in the background
      Promise.all(toMigrate.map(r => this.save(r)))
        .then(() => localStorage.removeItem(LEGACY_KEY))
        .catch(() => { /* keep localStorage if migration fails */ });
    } catch { /* ignore parse errors */ }
  }

  // ── Private utilities ────────────────────────────────────────────────────

  private mapType(directusType: string): ResolvedField['type'] {
    if (['integer', 'bigInteger', 'float', 'decimal'].includes(directusType)) return 'number';
    if (['timestamp', 'dateTime', 'date'].includes(directusType)) return 'datetime';
    if (directusType === 'boolean') return 'boolean';
    return 'string';
  }

  private humanise(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  private resolveNestedValue(row: Record<string, unknown>, path: string): string {
    const parts = path.split('.');
    let current: unknown = row;
    for (const part of parts) {
      if (current === null || current === undefined || typeof current !== 'object') return '(empty)';
      current = (current as Record<string, unknown>)[part];
    }
    return current === null || current === undefined ? '(empty)' : String(current);
  }

  private extractLabel(row: Record<string, unknown>, groupBy: string | null): string {
    if (!groupBy) return 'Total';
    if (groupBy.startsWith('month(')) {
      const raw = row['date_created_month'] ?? row['month'] ?? row['date_created'];
      const n = Number(raw);
      return (Number.isFinite(n) && n >= 1 && n <= 12)
        ? new Date(2000, n - 1).toLocaleString('default', { month: 'short' })
        : String(raw ?? '?');
    }
    if (groupBy.startsWith('year(')) {
      return String(row['date_created_year'] ?? row['year'] ?? row['date_created'] ?? '?');
    }
    const val = row[groupBy];
    return val === null || val === undefined ? '(empty)' : String(val);
  }

  private deriveGroupByLabel(groupBy: string | null): string {
    if (!groupBy) return 'Total';
    if (groupBy.startsWith('month(')) return 'Month';
    if (groupBy.startsWith('year('))  return 'Year';
    return this.humanise(groupBy);
  }
}
